import { FormControl } from '@angular/forms';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import { DvDropdownClientSideElement } from '@devinco/ui/legacy';
import { TranslateService } from '@ngx-translate/core';

export default class DynamicFormUtils {
  static getSelectedDataFromMultiselectDropdown<T>(
    element: DvDropdownClientSideElement,
    selectedItems: (string | number)[] | undefined,
    valueKey: keyof T
  ): T[] {
    const allItems = element.options.api?.getItems();
    const selectedObjects = allItems
      ?.filter(item => selectedItems?.includes(item[valueKey]))
      .map(item => item);

    return selectedObjects || [];
  }

  static getDropdownElementItems<T>(
    elements: DvDynamicFormElement[],
    dropdownElKey: string
  ): T[] {
    const element = elements.find(el => el.options.key === dropdownElKey) as
      | DvDropdownClientSideElement
      | undefined;

    return element?.options.api?.getItems() || [];
  }

  static setDropdownElementItems<T>(
    elements: DvDynamicFormElement[],
    dropdownElKey: string,
    items: T[]
  ): void {
    const element = elements.find(el => el.options.key === dropdownElKey) as
      | DvDropdownClientSideElement
      | undefined;

    if (element && !element.options.api) {
      element.options.ready = params => {
        params.api.setItems(items);
      };
    }

    element?.options.api?.setItems(items);
  }

  static setTranslatedDropdownElementItems<T>(
    elements: DvDynamicFormElement[],
    dropdownElKey: string,
    items: T[],
    translate: TranslateService,
    translationKeyParamName: keyof T,
    translationPrefix?: string,
    translationSuffix?: string
  ): void {
    const element = elements.find(el => el.options.key === dropdownElKey) as
      | DvDropdownClientSideElement
      | undefined;

    if (!element) return;

    const labelKey = element.options.labelKeys?.[0] as keyof T | undefined;
    const translatedItems = labelKey
      ? DynamicFormUtils.translateDropdownData(
          items,
          translate,
          labelKey,
          translationKeyParamName,
          translationPrefix,
          translationSuffix
        )
      : items;

    if (!element.options.api) {
      element.options.ready = params => {
        params.api.setItems(translatedItems);
      };
    }

    element.options.api?.setItems(translatedItems);
  }

  static translateDropdownData<T>(
    data: T[],
    translate: TranslateService,
    labelKey: keyof T,
    translationKeyParamName: keyof T,
    translationPrefix?: string,
    translationSuffix?: string
  ): T[] {
    return data.map(item => {
      if (!item[translationKeyParamName]) return item;
      let translationKey: string | T[keyof T] = translationPrefix
        ? `${translationPrefix}.${item[translationKeyParamName]}`
        : item[translationKeyParamName];

      if (translationSuffix) {
        translationKey = `${translationKey}.${translationSuffix}`;
      }

      item[labelKey] = translate.instant(
        translationKey as string
      ) as T[keyof T];

      return item;
    });
  }

  static enableDisableControlWithoutEvent(
    control: FormControl<unknown>,
    shouldEnable: boolean
  ): void {
    shouldEnable
      ? control.enable({ emitEvent: false })
      : control.disable({ emitEvent: false });
  }
}
